import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { SearchOffRounded } from '@mui/icons-material'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import { getYakurekiList } from '@/shared/apis/yakureki'
import { useToast } from '@/shared/contexts/ToastContext'

// 外部アプリから deep-link で着地するエントリポイント. 2 系統の検索キーをサポートする:
//   1. queue_number (= NSIPS 連携): /yakureki/quick-record?queue_number=033
//      その日・当該薬剤師がアップ済の yakureki を受付番号で検索 → /soap/:id へ遷移し
//      autoStart: true で録音を自動開始する.
//   2. patient_name (= AnyCOMPASS iframe 連携): /yakureki/quick-record?patient_name=山田太郎
//      該当患者リクエストを氏名で検索 → /soap/:id を開く (検索窓は通常一覧と同じ表示日数.
//      back: YakurekiEventViewSet.get_queryset). 録音の自動開始は既定で行わず (= 録音画面を
//      開くだけ), auto_start=1 指定時のみ開始する (= iframe を開いた瞬間の不意の録音開始を防ぐ).
// 両方指定時は queue_number を優先. 複数件ヒット時は created_at desc の先頭を採用.
// 着地先 IndexPage (legacy / latest) は history state を検出して録音を自動開始する.
// URL クエリには autoStart を載せない (= reload で二重発火しないため state 経由).
//
// 0 件・検索失敗時の帰結は経路で分ける:
//   - queue_number 経路: 従来どおり toast → landing へ戻す (単体 Kakeru の NSIPS 動線).
//   - patient_name 経路: この画面に留まり「該当なし」を明示する. landing へ戻すと
//     直近アクセスの薬歴 (= 別患者) へ redirect され、AnyCOMPASS 埋め込みでは患者名も
//     非表示のため前患者の画面が残ったように見える (= 患者取り違えの温床).
//
// 終端表示の文言は「患者名が見つからなかった」ことだけを述べる. 薬歴はホスト (AnyCOMPASS) 側の
// 資産であり、Kakeru が「薬歴が無い」と述べると所在を誤って伝えることになるため使わない.

type SearchPhase = 'searching' | 'notFound' | 'error'

const QuickRecordPage: FC = () => {
  const navigate = useNavigate()
  const { showToast } = useToast()
  const [searchParams] = useSearchParams()
  const queueNumber = searchParams.get('queue_number')
  const patientName = searchParams.get('patient_name')
  // queue_number は従来どおり常に自動開始. patient_name 経路は auto_start=1 指定時のみ.
  const autoStart = queueNumber ? true : searchParams.get('auto_start') === '1'

  const [phase, setPhase] = useState<SearchPhase>('searching')
  // 0 件・失敗時にこの画面へ留まるのは patient_name 経路のみ (queue_number は従来挙動).
  const staysOnNoHit = patientName !== null && queueNumber === null

  // phase のリセットは呼び出し側(再検索ボタン)が行う. 初回 effect では初期値が
  // 'searching' のため不要 (= effect 内の同期 setState を避ける. react-hooks/set-state-in-effect).
  const runSearch = useCallback(async () => {
    // queue_number を優先. どちらも無ければエラーで landing へ.
    const searchLabel = queueNumber
      ? `受付番号 ${queueNumber}`
      : patientName
      ? `患者名 ${patientName}`
      : null

    if (!searchLabel) {
      showToast('受付番号または患者名が指定されていません', 'error')
      // showToast + navigate を同一 tick で連続させると React 18 の batching で
      // Snackbar 開示 (= setOpen(true)) と route 変更が同じ render に押し込まれ、
      // toast が表示される前に LandingOrRedirect → /soap/:id へ連鎖遷移して visual に
      // 出ないことがある. ここだけは micro task 境界を 1 つ挟んで toast を先に
      // commit させる. (0件 / catch path は await があるので不要.)
      setTimeout(() => navigate('/', { replace: true }), 0)
      return
    }

    try {
      const res = await getYakurekiList({
        ...(queueNumber
          ? { queue_number: queueNumber }
          : { patient_name: patientName as string }),
        force_refresh: true,
      })
      const results = res.data.results

      if (results.length === 0) {
        if (staysOnNoHit) {
          setPhase('notFound')
          return
        }
        showToast(
          `${searchLabel} に該当する薬歴が見つかりませんでした`,
          'error'
        )
        navigate('/', { replace: true })
        return
      }

      if (results.length > 1) {
        // backend が created_at desc で並べる想定. 念のため先頭採用 + warn で
        // 通知のみ. UI は止めない.
        console.warn(
          `[quick-record] ${searchLabel} で ${results.length} 件ヒット. 先頭を採用します.`,
          results.map((r) => r.id)
        )
      }

      const yakurekiId = results[0].id
      // ?patient_name= は /soap/:id へも引き継ぐ。SSoT (EmbedContext.deeplinkPatientName)
      // は URL からしか再取得できず、表示中のフルリロード (認証退避の /login 遷移・手動
      // reload) で消えると突合ガードが沈黙するため、URL を SSoT の器として残す。
      // auto_start は載せない (= reload のたびの不意の録音開始を防ぐ。自動開始は
      // history state 経由の 1 回きり)。
      const query = patientName
        ? `?patient_name=${encodeURIComponent(patientName)}`
        : ''
      navigate(`/soap/${yakurekiId}${query}`, {
        replace: true,
        state: { autoStart },
      })
    } catch {
      if (staysOnNoHit) {
        setPhase('error')
        return
      }
      showToast('薬歴の検索に失敗しました', 'error')
      navigate('/', { replace: true })
    }
  }, [queueNumber, patientName, autoStart, staysOnNoHit, navigate, showToast])

  // StrictMode の double-invoke + 親 re-render 由来の重複発火を防ぐ
  // (= 検索 API は 1 回だけ叩く).
  const hasRun = useRef(false)

  // setTimeout(0) で commit 完了後に起動する (= effect 内の同期 setState を避ける
  // react-hooks/set-state-in-effect 規約. useRecordingStatus の reset と同型).
  // hasRun 判定を callback 側に置くことで StrictMode の mount→cleanup→mount でも
  // 2 個目の timer だけが 1 回発火する.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (hasRun.current) return
      hasRun.current = true
      void runSearch()
    }, 0)
    return () => window.clearTimeout(timer)
  }, [runSearch])

  if (phase !== 'searching') {
    const isNotFound = phase === 'notFound'
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        bgcolor="grey.100"
        gap={2}
        px={3}
        textAlign="center"
      >
        <SearchOffRounded sx={{ fontSize: 48, color: 'text.disabled' }} />
        <Typography variant="subtitle1" sx={{ color: 'text.primary' }}>
          {isNotFound
            ? `患者名「${patientName}」が見つかりませんでした`
            : '検索に失敗しました'}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            setPhase('searching')
            void runSearch()
          }}
        >
          {isNotFound ? '再検索' : '再試行'}
        </Button>
      </Box>
    )
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      bgcolor="grey.100"
      gap={2}
    >
      <CircularProgress size={48} thickness={4} />
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        薬歴を検索しています...
      </Typography>
    </Box>
  )
}

export default QuickRecordPage
